import {AppConfig} from 'AppConfig'
import {
  Box,
  Button,
  Container,
  IconButton,
  Stack,
  Typography,
  useTheme
} from '@mui/material'
import FacebookIcon from '@mui/icons-material/Facebook'
import InstagramIcon from '@mui/icons-material/Instagram'
import YouTubeIcon from '@mui/icons-material/YouTube'
import LinkedInIcon from '@mui/icons-material/LinkedIn'
import NorthEastIcon from '@mui/icons-material/NorthEast'
import footerRobo from '../assets/footer-robo.png'
import logo from '../assets/logo.svg'
import {useLocation, useNavigate} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import Guard from 'routes/Guard'
import kc from 'web-common/services/auth'
import LoginGuard from 'routes/LoginGuard'

const FooterSection = () => {
  const theme = useTheme()
  const [t] = useTranslation()

  const navigate = useNavigate()
  const location = useLocation()

  const openGuardedLink = async (path: string, guard?: Guard) => {
    let url = path

    if (guard) {
      const hasPermission = await guard.hasPermission()
      if (!hasPermission) {
        const fallbackUrl = guard.getFallbackUrl(location)
        if (fallbackUrl === '/login') {
          await kc.automaticLoginFlow(window.location.origin + path)
          return
        } else {
          url = fallbackUrl
        }
      }
    }

    navigate(url)
  }

  const startChatNow = () => {
    openGuardedLink('/chat', LoginGuard)
  }

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: theme.palette.common.black,
        color: 'white'
      }}
    >
      <Container
        maxWidth="lg"
        sx={{
          backgroundColor: theme.palette.common.black,
          marginTop: '-8px',
          paddingTop: {xs: '50px', lg: '260px'}
        }}
      >
        <Box
          sx={{
            position: 'relative',
            py: {xs: '10px', lg: '80px'},
            textAlign: {xs: 'center', md: 'left'},
            display: 'flex',
            flexDirection: 'column-reverse',
            justifyContent: 'space-between',
            gap: '50px'
          }}
        >
          <Box
            component="div"
            sx={{
              position: 'absolute',
              top: {xs: '45%', sm: '20%', lg: '20%'},
              left: '50%',
              width: {xs: '50%', sm: '15%', lg: '18%'},
              height: 'auto',
              aspectRatio: '1 / 1',
              borderRadius: '50%',
              backgroundColor: theme.palette.primary.main,
              filter: 'blur(100px)',
              zIndex: 1,
              transform: 'translate(-50%, -50%)'
            }}
          />
          <Stack
            spacing={3}
            sx={{
              alignItems: {
                xs: 'center',
                lg: 'flex-start'
              },
              maxWidth: 400,
              zIndex: 3,
              position: 'relative',
              marginX: {xs: 'auto', lg: '0'}
            }}
          >
            <Box
              component="img"
              src={logo}
              sx={{width: 230, height: 44, marginBottom: 1}}
            />
            <Typography
              sx={{
                fontSize: 14,
                color: theme.palette.grey[400],
                maxWidth: 300
              }}
            >
              {t('fnd-home-footer-slogan')}
            </Typography>
            <Stack direction="row" spacing={1}>
              <IconButton
                href="https://www.facebook.com/Pinderellapp/"
                target="_blank"
                sx={{
                  color: theme.palette.grey[400],
                  '&:hover': {color: theme.palette.common.white}
                }}
              >
                <FacebookIcon />
              </IconButton>
              <IconButton
                href="https://www.instagram.com/finderellapp/"
                target="_blank"
                sx={{
                  color: theme.palette.grey[400],
                  '&:hover': {color: theme.palette.common.white}
                }}
              >
                <InstagramIcon />
              </IconButton>
              <IconButton
                href="https://m.youtube.com/channel/UCHVKKxUlE-XE1r3ViPoQs1Q"
                target="_blank"
                sx={{
                  color: theme.palette.grey[400],
                  '&:hover': {color: theme.palette.common.white}
                }}
              >
                <YouTubeIcon />
              </IconButton>
              <IconButton
                href="https://www.linkedin.com/in/%D7%A4%D7%99%D7%A0%D7%93%D7%A8%D7%9C%D7%94-%D7%9E%D7%A0%D7%95%D7%A2-%D7%9E%D7%A6%D7%99%D7%90%D7%94-529564142"
                target="_blank"
                sx={{
                  color: theme.palette.grey[400],
                  '&:hover': {color: theme.palette.common.white}
                }}
              >
                <LinkedInIcon />
              </IconButton>
            </Stack>
          </Stack>

          <Box
            component="img"
            src={footerRobo}
            sx={{
              width: {xs: '100%', sm: '100%', lg: 500},
              maxWidth: {xs: '100vw', sm: 400, lg: 'none'},
              marginX: 'auto',
              position: {
                xs: 'relative',
                sm: 'relative',
                md: 'relative',
                lg: 'absolute'
              },
              top: {lg: '20%'},
              left: {lg: '50%'},
              transform: {lg: 'translate(-50%, -50%)'},
              zIndex: 2
            }}
          />

          <Box
            sx={{
              marginTop: {xs: 4, sm: 0},
              position: {xs: 'static', sm: 'absolute'},
              top: {sm: '0%'},
              right: {sm: '5%'},
              transform: {xs: 'translateX(0)', sm: 'translateY(-50%)'},
              zIndex: 3,
              display: 'flex',
              justifyContent: {xs: 'center', sm: 'flex-end'},
              width: {xs: '100%', sm: 'auto'}
            }}
          >
            <Button
              variant="outlined"
              color="primary"
              endIcon={<NorthEastIcon />}
              onClick={startChatNow}
              sx={{
                borderRadius: 10,
                border: `1px solid ${theme.palette.common.white}`,
                color: theme.palette.common.white,
                backgroundColor: 'transparent',
                paddingX: 2,
                paddingY: 1,
                fontWeight: 500,
                '&:hover': {backgroundColor: theme.palette.grey[900]}
              }}
            >
              {t('fnd-home-footer-start-chat-now')}
            </Button>
          </Box>
        </Box>

        <Box
          sx={{
            height: '1px',
            background: `linear-gradient(to right, ${theme.palette.common.black}, ${theme.palette.common.white}, ${theme.palette.common.black})`,
            width: '100%'
          }}
        />

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexDirection: {xs: 'column', sm: 'row'},
            paddingY: 2,
            gap: 2
          }}
        >
          <Typography variant="caption" sx={{color: theme.palette.grey[500]}}>
            © {new Date().getFullYear()} {t('fnd-home-footer-copyrights')}
          </Typography>
          <Stack direction="row" spacing={2}>
            <Typography
              component="a"
              href={`${AppConfig.publicDir}/docs/he/Accessibility_Statement.html`}
              target="_blank"
              variant="caption"
              sx={{
                color: theme.palette.grey[500],
                textDecoration: 'none',
                '&:hover': {
                  color: theme.palette.common.white,
                  textDecoration: 'underline'
                }
              }}
            >
              {t('fnd-home-footer-link-1')}
            </Typography>
            <Typography
              component="a"
              href={`${AppConfig.publicDir}/docs/he/Terms_and_Conditions_of_Use-Hebrew.html`}
              target="_blank"
              variant="caption"
              sx={{
                color: theme.palette.grey[500],
                textDecoration: 'none',
                '&:hover': {
                  color: theme.palette.common.white,
                  textDecoration: 'underline'
                }
              }}
            >
              {t('fnd-home-footer-link-2')}
            </Typography>
            <Typography
              component="a"
              href={`${AppConfig.publicDir}/docs/he/Privacy_Policy_Hebrew.html`}
              target="_blank"
              variant="caption"
              sx={{
                color: theme.palette.grey[500],
                textDecoration: 'none',
                '&:hover': {
                  color: theme.palette.common.white,
                  textDecoration: 'underline'
                }
              }}
            >
              {t('fnd-home-footer-link-3')}
            </Typography>
          </Stack>
        </Box>
      </Container>
    </Box>
  )
}

export default FooterSection
